import {
  type Query,
  type Requester,
  list,
  listWithParams,
  operationContract as operation,
  record,
  requestBlobContract
} from "./coaching-services-transport";
import { analyticsServices, reportFollowThroughServices } from "./coaching-followthrough-services";
import { composedDashboardReadModel } from "./coaching-dashboard-read-service";
import type { CoachingRole } from "../domain/roles";
import type {
  CoachingArtifactDto,
  CoachingParticipantDto,
  CoachingRecordInput,
  CoachingSessionDto,
  FutureSeriesRescheduleRequest,
  InviteOperationRequest,
  ParticipantBulkImportRequest,
  SessionLifecycleRequest,
  SessionRescheduleRequest,
  SessionSeriesPreviewRequest,
  SessionSeriesRequest
} from "./coaching-dtos";

export function createParticipantsService(requester: Requester) {
  return {
    list: (query?: Query) => list<CoachingParticipantDto>(requester, "participants.list", query),
    create: (body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingParticipantDto>(requester, "participants.create", { body, idempotencyKey }),
    listForCohort: (cohortId: string, query?: Query) =>
      listWithParams<CoachingParticipantDto>(requester, "participants.listForCohort", {
        pathParams: { cohort_id: cohortId },
        query
      }),
    update: (cohortId: string, participantId: string, body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingParticipantDto>(requester, "participants.update", {
        pathParams: { cohort_id: cohortId, participant_id: participantId },
        body,
        idempotencyKey
      }),
    removeFromCohort: (cohortId: string, participantId: string, idempotencyKey: string) =>
      operation(requester, "participants.removeFromCohort", { pathParams: { cohort_id: cohortId, participant_id: participantId }, idempotencyKey }),
    attachToCohort: (cohortId: string, body: CoachingRecordInput, idempotencyKey: string) =>
      operation(requester, "participants.attachToCohort", {
        pathParams: { cohort_id: cohortId },
        body,
        idempotencyKey
      }),
    assignMembership: (cohortId: string, body: CoachingRecordInput, idempotencyKey: string) =>
      operation(requester, "participants.assignMembership", {
        pathParams: { cohort_id: cohortId },
        body,
        idempotencyKey
      }),
    bulkImport: (cohortId: string, body: ParticipantBulkImportRequest, idempotencyKey: string) =>
      operation(requester, "participants.bulkImport", {
        pathParams: { cohort_id: cohortId },
        body,
        idempotencyKey
      })
  };
}

export function createSessionsService(requester: Requester) {
  return {
    list: (query?: Query) => list<CoachingSessionDto>(requester, "sessions.list", query),
    calendar: (query: Query) => list<CoachingSessionDto>(requester, "sessions.calendar", query),
    create: (body: CoachingRecordInput, idempotencyKey: string) =>
      record<CoachingSessionDto>(requester, "sessions.create", { body, idempotencyKey }),
    listForCohort: (cohortId: string, query?: Query) =>
      listWithParams<CoachingSessionDto>(requester, "sessions.listForCohort", {
        pathParams: { cohort_id: cohortId },
        query
      }),
    previewSeries: (cohortId: string, body: SessionSeriesPreviewRequest, idempotencyKey: string) =>
      operation(requester, "sessions.previewSeries", {
        pathParams: { cohort_id: cohortId },
        body,
        idempotencyKey
      }),
    createSeries: (cohortId: string, body: SessionSeriesRequest, idempotencyKey: string) =>
      operation(requester, "sessions.createSeries", {
        pathParams: { cohort_id: cohortId },
        body,
        idempotencyKey
      }),
    rescheduleFuture: (cohortId: string, body: FutureSeriesRescheduleRequest, idempotencyKey: string) =>
      operation(requester, "sessions.rescheduleFuture", {
        pathParams: { cohort_id: cohortId },
        body,
        idempotencyKey
      }),
    detail: (sessionId: string) =>
      record<CoachingSessionDto>(requester, "sessions.detail", { pathParams: { session_id: sessionId } }),
    sendInvites: (sessionId: string, body: InviteOperationRequest, idempotencyKey: string) =>
      operation(requester, "sessions.sendInvites", {
        pathParams: { session_id: sessionId },
        body,
        idempotencyKey
      }),
    forceResend: (sessionId: string, body: InviteOperationRequest, idempotencyKey: string) =>
      operation(requester, "sessions.forceResend", {
        pathParams: { session_id: sessionId },
        body,
        idempotencyKey
      }),
    rescheduleOne: (sessionId: string, body: SessionRescheduleRequest, idempotencyKey: string) =>
      operation(requester, "sessions.rescheduleOne", {
        pathParams: { session_id: sessionId },
        body,
        idempotencyKey
      }),
    syncZoom: (sessionId: string, idempotencyKey: string) =>
      operation(requester, "sessions.syncZoom", {
        pathParams: { session_id: sessionId },
        idempotencyKey
      }),
    reassignCoach: (sessionId: string, body: CoachingRecordInput, idempotencyKey: string) =>
      operation(requester, "sessions.reassignCoach", {
        pathParams: { session_id: sessionId },
        body,
        idempotencyKey
      }),
    lifecycle: (sessionId: string, body: SessionLifecycleRequest, idempotencyKey: string) =>
      operation(requester, "sessions.lifecycle", { pathParams: { session_id: sessionId }, body, idempotencyKey })
  };
}

export function createReportsService(requester: Requester) {
  return {
    list: (query?: Query) => list<CoachingArtifactDto>(requester, "reports.list", query),
    sessionReport: (sessionId: string) =>
      record<CoachingArtifactDto>(requester, "reports.sessionReport", {
        pathParams: { session_id: sessionId }
      }),
    sessionReportDownload: (sessionId: string, reportId: string) =>
      requestBlobContract(requester, "reports.sessionReportDownload", {
        pathParams: { session_id: sessionId },
        query: { reportId }
      }),
    sessionTranscript: (sessionId: string) =>
      record<CoachingArtifactDto>(requester, "reports.sessionTranscript", {
        pathParams: { session_id: sessionId }
      }),
    // Phase 4 follow-through (mark-reviewed write-back + CSV export). The
    // backend re-applies the D-3 guard on review, so a company_admin can never
    // review a confidential report (403). Extracted to keep this facade under
    // the LOC ceiling (the documented facade-split direction).
    ...reportFollowThroughServices(requester)
  };
}

export function createDashboardService(requester: Requester) {
  return {
    composedRead: (role: CoachingRole) => composedDashboardReadModel(requester, list, role)
  };
}

export { analyticsServices };
